import { m as motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Star } from "lucide-react";
import { fadeIn, fadeInUp, defaultViewport, defaultTransition, fastTransition } from "@/lib/motion";
import { EXTERNAL_LINKS, MEMBERSHIP_LINKS } from "../lib/constants";
import {
  EARLY_BIRD_DISCOUNT_CODE,
  EARLY_BIRD_DISCOUNT_LABEL,
  NEXT_COHORT_MEMBERSHIP_LABEL,
  NEXT_COHORT_PATH,
  PREVIOUS_COHORT_STATUS,
} from "@/lib/cohorts";

interface Tier {
  name: string;
  subtitle?: string;
  badge?: string | null;
  borderColor: string;
  checkColor: string;
  monthlyPrice: number;
  annualPrice: number;
  link: string;
  buttonText: string;
  buttonVariant: "default" | "secondary";
  features: React.ReactNode[];
  cohortInfo?: {
    spring: string;
    fall: string;
  };
}

const tiers: Tier[] = [
  {
    name: "Free Membership",
    subtitle: "(private community access)",
    badge: null,
    borderColor: "border-secondary/30",
    checkColor: "text-secondary",
    monthlyPrice: 0,
    annualPrice: 0,
    link: MEMBERSHIP_LINKS.PORTAL,
    buttonText: "Get Started",
    buttonVariant: "secondary" as const,
    features: [
      <>
        Monthly live workshops <em>(hands-on with top AI tools)</em>
      </>, <>
        Collection of free implementation guides
      </>
    ],
  },
  {
    name: "Standard Membership",
    subtitle: "(Self-Paced)",
    badge: null,
    borderColor: "border-secondary/40",
    checkColor: "text-secondary",
    monthlyPrice: 30,
    annualPrice: 360,
    link: MEMBERSHIP_LINKS.STANDARD,
    buttonText: "Join Now",
    buttonVariant: "secondary" as const,
    features: [<>
      The Core AI for HR Mastermind course
    </>, <>
      18+ actionable implementation guides
    </>, <>
      Monthly live workshops <em>(hands-on with top AI tools)</em>
    </>, <>
      Workshop recording library
    </>, <>
      Full access to AI Chat Platform with the top 6 frontier AI models
    </>, <>
      JD Pro & Intern Manager & Mastermind Assistant (Workplace Labs custom AI agents)
    </>, <>
      Private community access
    </>, <>
      Certificate of completion
    </>]
  },
  {
    name: "Premium Membership",
    subtitle: "(Lifetime Access + Live Cohorts)",
    badge: "Most Popular",
    borderColor: "border-primary/40",
    checkColor: "text-primary",
    monthlyPrice: 117,
    annualPrice: 1400,
    link: EXTERNAL_LINKS.PREMIUM_MASTERMIND_APPLICATION,
    buttonText: "Apply Now",
    buttonVariant: "default" as const,
    features: [<>
      Everything in Standard, plus:
    </>, <>
      <em>Limited to 200 members</em>
    </>, <>
      {EARLY_BIRD_DISCOUNT_LABEL} with code <strong>{EARLY_BIRD_DISCOUNT_CODE}</strong>
    </>, <>
      An AI Readiness assessment
      </>, <>
      Lifetime access to all content and future updates
    </>, <>
      2 Cohort Intensives per year <em>(learn and problem-solve with HR peers)</em>
    </>, <>
      VIP community access
    </>, <>
      Monthly tech clinics <em>(group coaching for scaling your AI initiatives)</em>
    </>, <>
      Quarterly guest expert sessions
    </>],
    cohortInfo: {
      spring: PREVIOUS_COHORT_STATUS,
      fall: NEXT_COHORT_MEMBERSHIP_LABEL,
    }
  },
];

export const MembershipTiersSection = () => {
  return (
    <motion.section
      id="pricing"
      initial={fadeIn.initial}
      whileInView={fadeIn.animate}
      viewport={defaultViewport}
      transition={defaultTransition}
      className="bg-foreground container-class relative overflow-hidden scroll-mt-24"
    >
      <div className="section-inner relative z-10">
        <motion.div
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          viewport={defaultViewport}
          transition={{ ...defaultTransition, delay: 0.2 }}
          className="space-y-8"
        >
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-background">
              <span className="gradient-text">Membership Tiers</span>
            </h2>
            <p className="text-lg md:text-xl text-background/90 max-w-2xl mx-auto">
              Choose the membership level that fits your learning style and career goals
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3 max-w-7xl mx-auto">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={fadeInUp.initial}
                whileInView={fadeInUp.animate}
                viewport={defaultViewport}
                transition={{ ...fastTransition, delay: 0.3 + index * 0.08 }}
              >
                <Card className={`h-full flex flex-col bg-card hover-lift border-2 ${tier.borderColor} relative`}>
                  {/* Badge */}
                  {tier.badge && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-sm font-semibold ${
                        tier.badge === "Most Popular"
                          ? "bg-gradient-secondary"
                          : "bg-gradient-primary"
                      }`}>
                        <Star className="w-4 h-4 fill-white" />
                        {tier.badge}
                      </div>
                    </div>
                  )}

                  <CardHeader className={`${tier.badge ? 'pt-8' : 'pt-6'} pb-4`}>
                    <CardTitle className="text-2xl font-bold text-center">
                      {tier.name}
                    </CardTitle>
                    {tier.subtitle && (
                      <p className="text-base font-normal text-muted-foreground mt-2 text-center">
                        {tier.subtitle}
                      </p>
                    )}
                    
                    {/* Pricing */}
                    {tier.monthlyPrice > 0 && (
                      <div className="text-center mt-4">
                        <div className={`text-4xl font-bold bg-clip-text text-transparent ${
                          tier.badge === "Most Popular"
                            ? "bg-gradient-secondary"
                            : "bg-gradient-primary"
                        }`}>
                          ${tier.monthlyPrice}/month
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {tier.name === "Premium Membership" ? "Billed once at" : "Billed annually at"} ${tier.annualPrice}
                        </p>
                      </div>
                    )}
                    {tier.monthlyPrice === 0 && (
                      <div className="text-center mt-4">
                        <div className="text-4xl font-bold text-secondary">
                          Free
                        </div>
                      </div>
                    )}
                  </CardHeader>

                  <CardContent className="flex-grow px-6">
                    <ul className="space-y-3">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 ${tier.checkColor} mt-0.5 flex-shrink-0`} />
                          <span className="text-sm leading-relaxed text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  {/* Cohort Info Callout - Premium Only */}
                  {tier.cohortInfo && (
                    <div className="mx-6 mb-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">2026 Cohort Calendar</p>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">
                          🌱 Spring: <span className="text-primary font-semibold">{tier.cohortInfo.spring}</span>
                        </p>
                        <p className="text-sm font-medium text-foreground">
                          🍂 Next up: <span className="text-primary font-semibold">{tier.cohortInfo.fall}</span>
                        </p>
                      </div>
                      <Link
                        to={NEXT_COHORT_PATH}
                        className="mt-3 inline-flex text-sm font-medium text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
                      >
                        Learn about the next cohort
                      </Link>
                    </div>
                  )}

                  <CardFooter className="flex flex-col gap-2 px-6 pb-6">
                    <Button
                      asChild
                      variant={tier.buttonVariant}
                      className="w-full shadow-lg hover:shadow-glow-primary transition-all duration-300"
                      size="lg"
                    >
                      <a href={tier.link} target="_blank" rel="noopener noreferrer">
                        {tier.buttonText}
                      </a>
                    </Button>

                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/team-training" className="text-sm text-background/80 hover:text-background transition-colors underline underline-offset-4">
              Need to train your whole team?
            </Link>
          </div>
        </motion.div>
      </div>
      
    </motion.section>
  );
};
